import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, Package } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Cart = ({ cart, updateQuantity, removeFromCart, clearCart }) => {
  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      if (item.isBundle) {
        return total + (item.price * item.quantity);
      }
      return total + (item.price * item.quantity);
    }, 0);
  };

  const calculateSavings = () => {
    let savings = 0;
    cart.forEach(item => {
      if (item.isBundle && item.package) {
        savings += item.package.savings * item.quantity;
      }
    });
    return savings;
  };

  const savings = calculateSavings();
  const total = calculateTotal();
  const finalTotal = total;

  if (cart.length === 0) {
    return (
      <div className="cart-empty">
        <div className="container">
          <div className="empty-state">
            <ShoppingBag size={64} className="empty-icon" />
            <h2 className="empty-title">Your cart is empty</h2>
            <p className="empty-text">
              Discover our wellness scents and start your journey to natural aromatherapy
            </p>
            <Link to="/" className="btn-primary btn-cta">
              Explore Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <div className="cart-header">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            Continue Shopping
          </Link>
          <h1 className="page-title">Shopping Cart</h1>
          <p className="cart-count">{cart.length} {cart.length === 1 ? 'item' : 'items'}</p>
        </div>

        <div className="cart-layout">
          <div className="cart-items">
            {cart.map((item) => (
              <Card key={item.bundleId || item.id} className="cart-item-card">
                <CardContent className="cart-item-content">
                  {item.isBundle ? (
                    <>
                      <div className="bundle-badge">
                        <Package size={20} />
                        <span>Bundle</span>
                      </div>
                      <div className="cart-bundle-details">
                        <div className="cart-item-info">
                          <h3 className="cart-item-name">{item.name}</h3>
                          <p className="cart-item-description">
                            {item.products.length} products included
                          </p>
                          <div className="bundle-products-list">
                            {item.products.map((product, idx) => (
                              <div key={idx} className="bundle-product-item">
                                <img src={product.images[0]} alt={product.name} className="bundle-product-thumb" />
                                <span>{product.icon} {product.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="cart-item-actions">
                          <div className="quantity-controls">
                            <button 
                              className="quantity-btn"
                              onClick={() => updateQuantity(item.id, item.bundleId, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus size={16} />
                            </button>
                            <span className="quantity-value">{item.quantity}</span>
                            <button 
                              className="quantity-btn"
                              onClick={() => updateQuantity(item.id, item.bundleId, item.quantity + 1)}
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                          <div className="cart-item-price">₹{item.price * item.quantity}</div>
                          <button 
                            className="remove-btn"
                            onClick={() => removeFromCart(item.id, item.bundleId)}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <img 
                        src={item.images[0]} 
                        alt={item.name}
                        className="cart-item-image"
                      />
                      <div className="cart-item-details">
                        <div className="cart-item-info">
                          <h3 className="cart-item-name">{item.name}</h3>
                          <p className="cart-item-description">{item.description}</p>
                        </div>
                        <div className="cart-item-actions">
                          <div className="quantity-controls">
                            <button 
                              className="quantity-btn"
                              onClick={() => updateQuantity(item.id, null, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus size={16} />
                            </button>
                            <span className="quantity-value">{item.quantity}</span>
                            <button 
                              className="quantity-btn"
                              onClick={() => updateQuantity(item.id, null, item.quantity + 1)}
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                          <div className="cart-item-price">₹{item.price * item.quantity}</div>
                          <button 
                            className="remove-btn"
                            onClick={() => removeFromCart(item.id, null)}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="cart-summary-wrapper">
            <Card className="cart-summary">
              <CardContent className="summary-content">
                <h2 className="summary-title">Order Summary</h2>
                
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>₹{total}</span>
                </div>

                {savings > 0 && (
                  <div className="summary-row savings">
                    <span>Bundle Savings</span>
                    <span>-₹{savings}</span>
                  </div>
                )}

                <div className="summary-divider"></div>

                <div className="summary-row total">
                  <span>Total</span>
                  <span>₹{finalTotal}</span>
                </div>

                <button className="btn-primary btn-cta" style={{ width: '100%', marginTop: '16px' }}>
                  Proceed to Checkout
                </button>

                <button 
                  className="clear-cart-btn"
                  onClick={clearCart}
                >
                  Clear Cart
                </button>

                <div className="trust-badges">
                  <div className="trust-badge">
                    <span>♻️</span>
                    <span>100% Upcycled</span>
                  </div>
                  <div className="trust-badge">
                    <span>🌱</span>
                    <span>Natural Oils</span>
                  </div>
                  <div className="trust-badge">
                    <span>🇮🇳</span>
                    <span>Made in India</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
